import { Controller } from "@hotwired/stimulus"

// Core::TabsComponent — a bespoke, accessible tabs primitive.
//
// Per ADR MC-425 we import no Flowbite JS: the roving tabindex, the
// arrow/Home/End keyboard navigation, and the live tab/panel state are all
// implemented here, against the interactive-primitive contract in
// docs/13-ui-conventions.md (Roving tabindex + ARIA wiring rows).
//
// Lifecycle:
//   - connect() is idempotent and self-contained. It reconciles the strip to
//     the active tab encoded in the DOM (the tab carrying
//     `aria-selected="true"`, else the first) — so a Turbo Drive navigation,
//     a `<turbo-frame>` update, or a Turbo-cache snapshot that paints a stale
//     active tab is corrected on connect. All state is read from the DOM on
//     use; nothing is held in module- or closure-level state that would
//     outlive a navigation. Events are handled via Stimulus actions
//     (`click->core--tabs#select`, `keydown->core--tabs#navigate`) declared on
//     each tab target, so no manual addEventListener is needed.
//
// Selection model: activation follows focus — moving with the arrow keys (or
// Home/End) both moves focus and activates the tab, the single-tab-stop
// roving-tabindex pattern. The active tab is the only tab stop
// (`tabindex="0"`); the rest are `-1`.
export default class extends Controller {
  static targets = ["tab", "panel"]

  connect() {
    // Reconcile to the DOM-derived active tab (a cache-restored snapshot may
    // disagree with the live attributes). No-op when already consistent.
    this.activate(this.selectedIndex(), { focus: false })
  }

  // Click on a tab: activate the tab that was hit.
  select(event) {
    const index = this.tabTargets.indexOf(event.currentTarget)
    if (index === -1) return
    event.preventDefault()
    this.activate(index, { focus: true })
  }

  // Arrow / Home / End drive the roving tabindex; Enter/Space activate the
  // focused tab (a no-op under activation-follows-focus, but kept so the
  // keys don't fall through). Other keys pass through untouched.
  navigate(event) {
    const current = this.tabTargets.indexOf(event.target)
    if (current === -1) return

    const last = this.tabTargets.length - 1
    let next

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = current === last ? 0 : current + 1
        break
      case "ArrowLeft":
      case "ArrowUp":
        next = current === 0 ? last : current - 1
        break
      case "Home":
        next = 0
        break
      case "End":
        next = last
        break
      case "Enter":
      case " ":
        event.preventDefault()
        this.activate(current, { focus: true })
        return
      default:
        return
    }

    event.preventDefault()
    this.activate(next, { focus: true })
  }

  // --- state -------------------------------------------------------------

  // The active tab index, read live from the DOM. Falls back to the first
  // tab when none is marked selected (e.g. a malformed snapshot).
  selectedIndex() {
    const index = this.tabTargets.findIndex(
      (tab) => tab.getAttribute("aria-selected") === "true"
    )
    return index === -1 ? 0 : index
  }

  // Make `index` the single active tab: it becomes the lone tab stop
  // (`tabindex="0"`, `aria-selected="true"`), every other tab steps back
  // (`tabindex="-1"`, `aria-selected="false"`), and the matching panel is
  // revealed while the rest are hidden. ARIA is mutated live, not just at
  // first render.
  activate(index, { focus }) {
    this.tabTargets.forEach((tab, i) => {
      const active = i === index
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.setAttribute("tabindex", active ? "0" : "-1")
      tab.classList.toggle("tabs__tab--active", active)
    })

    this.panelTargets.forEach((panel, i) => {
      const active = i === index
      panel.hidden = !active
      panel.classList.toggle("tabs__panel--active", active)
    })

    if (focus && this.tabTargets[index]) this.tabTargets[index].focus()
  }
}
